import { FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addToast, Button } from '@heroui/react'
import { usePostHog } from '@posthog/react'
import {
  loadStripe,
  StripeElementLocale,
  StripeElementsOptions
} from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import { useTheme } from '@/ui/theme'
import type { ProcessStartResponse } from '@itsrever/returns-api-types'
import { notifyError } from '@/services/sentry'
import { usePages } from '@/hooks/usePages'
import { useAppSelector } from '@/redux/hooks'
import { useTranslate } from '@/hooks'

const getStripeOptions = (
  clientSecret: string,
  locale: StripeElementLocale
) => {
  const origin = window.location.origin

  return {
    clientSecret,
    locale,
    fonts: [
      {
        family: 'Roobert',
        src: `url(${origin}/fonts/roobert/Roobert-Regular.woff2)`,
        weight: '400',
        style: 'normal'
      },
      {
        family: 'Roobert',
        src: `url(${origin}/fonts/roobert/Roobert-Medium.woff2)`,
        weight: '500',
        style: 'normal'
      },
      {
        family: 'Roobert',
        src: `url(${origin}/fonts/roobert/Roobert-SemiBold.woff2)`,
        weight: '600',
        style: 'normal'
      },
      {
        family: 'Roobert',
        src: `url(${origin}/fonts/roobert/Roobert-Bold.woff2)`,
        weight: '700',
        style: 'normal'
      }
    ],
    appearance: {
      theme: 'stripe',
      variables: {
        fontFamily: 'Roobert, sans-serif'
      },
      rules: {
        '.Label': {
          fontSize: '0.875rem'
        },
        '.Input': {
          fontSize: '0.875rem'
        },
        '.TermsText': {
          fontSize: '0.75rem'
        }
      }
    }
  } satisfies StripeElementsOptions
}

interface StripeCheckoutProps {
  process: ProcessStartResponse
  paymentAmount: number
}

export const StripeCheckout = ({
  process,
  paymentAmount
}: StripeCheckoutProps) => {
  const {
    i18n: { language }
  } = useTranslation()
  const { translate } = useTranslate()

  const stripePublishableKey = process.payment_frontend_api_key
  const clientSecret = process.payment_session_id

  const stripePromise = useMemo(() => {
    if (!stripePublishableKey) return null
    return loadStripe(stripePublishableKey, {
      developerTools: {
        assistant: {
          enabled: import.meta.env.DEV
        }
      }
    })
  }, [stripePublishableKey])

  const stripeOptions = useMemo(() => {
    if (!clientSecret) return null
    return getStripeOptions(clientSecret, language as StripeElementLocale)
  }, [clientSecret, language])

  if (!stripePublishableKey || !clientSecret || !stripeOptions) return null

  return (
    <div className="space-y-4">
      <div className="space-y-0.5">
        <h2
          className="text-base font-medium"
          dangerouslySetInnerHTML={{
            __html: translate('confirmation_page_payment_title')
          }}
        />
        <h3
          className="text-[0.8125rem] font-normal leading-normal text-neutral-500"
          dangerouslySetInnerHTML={{
            __html: translate('confirmation_page_payment_subtitle')
          }}
        />
      </div>
      <div>
        <Elements stripe={stripePromise} options={stripeOptions}>
          <StripePaymentForm
            processId={process.pid}
            paymentAmount={paymentAmount}
          />
        </Elements>
      </div>
    </div>
  )
}

const StripePaymentForm = ({
  processId,
  paymentAmount
}: {
  processId?: string
  paymentAmount: number
}) => {
  const { pages } = usePages()
  const { translate } = useTranslate()
  const {
    i18n: { language }
  } = useTranslation()

  const theme = useTheme()

  const posthog = usePostHog()

  const [loading, setLoading] = useState(false)

  const order = useAppSelector((store) => store.returnsApi.order.response)

  const stripe = useStripe()
  const elements = useElements()

  const handlePayment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!stripe || !elements) return

    posthog.capture('Payment Process Started', {
      checkout_id: order.checkout_id,
      process_id: processId
    })

    setLoading(true)

    const successParams = new URLSearchParams({
      email: order.order?.customer?.email ?? '',
      orderNumber: order.order?.identification?.customer_printed_order_id || '',
      checkoutId: order.checkout_id || '',
      processId: processId || ''
    })

    const baseURL = window.location.origin + pages.SuccessTracking

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${baseURL}?${successParams.toString()}`
      }
    })

    if (error) {
      if (error.type !== 'validation_error') {
        addToast({
          description: error.message ?? translate('stripe_checkout_wrong'),
          color: 'danger'
        })
      }

      if (
        error.type !== 'validation_error' &&
        error.type !== 'card_error' &&
        error.type !== 'invalid_request_error'
      )
        notifyError(error)
    }

    setLoading(false)
  }

  const color = theme.colors.primary.main
  const textColor = theme.colors.primary.contrastText

  return (
    <form onSubmit={handlePayment} className="space-y-6">
      <PaymentElement options={{ layout: 'tabs' }} />
      <Button
        type="submit"
        isLoading={loading}
        fullWidth
        radius="sm"
        size="lg"
        className="text-sm"
        style={{
          backgroundColor: color,
          color: textColor,
          borderRadius: theme.borderRadius
        }}
      >
        {translate('confirmation_page_payment_button')}{' '}
        {(
          paymentAmount /
          (order.order?.money_format?.amount_multiplied_by ?? 100)
        ).toLocaleString(language, {
          style: 'currency',
          currency: order.order?.money_format?.currency
        })}
      </Button>
    </form>
  )
}
